import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { feature, mesh } from 'topojson-client';
import { Box } from '@mui/material';
import { useTheme } from '../../context/ThemeContext';
import {
  createPointClusters,
  formatCodeAmount,
  getClusterRadius,
  getFallbackOffset,
  getPointRadius,
  getStateInitials,
  getStateName
} from '../../utils/analytics';
import statesData from '../../assets/states-10m.json';

const MAP_WIDTH = 1120;
const MAP_HEIGHT = 660;
const PROVIDER_LABEL_WIDTH = 138;
const PROVIDER_LABEL_HEIGHT = 30;
const DEFAULT_SPECIFICATION = {
  color: '#48a99a',
  size: 5,
  fillOpacity: 0.65,
  strokeOpacity: 0.8,
  strokeWidth: 1
};
const STATES = feature(statesData, statesData.objects.states);
const STATE_FEATURES = STATES.features;
const STATE_BORDERS = mesh(statesData, statesData.objects.states, (a, b) => a !== b);
const MAP_PROJECTION = d3.geoAlbersUsa()
  .fitExtent([[24, 24], [MAP_WIDTH - 24, MAP_HEIGHT - 24]], STATES);
const MAP_PATH = d3.geoPath(MAP_PROJECTION);
const STATE_RENDER_FEATURES = STATE_FEATURES.map((stateFeature) => {
  return {
    feature: stateFeature,
    name: stateFeature.properties.name,
    initials: getStateInitials(stateFeature.properties.name),
    pathD: MAP_PATH(stateFeature),
    centroid: MAP_PATH.centroid(stateFeature)
  };
});
const STATE_SPATIAL_ENTRIES = STATE_FEATURES
  .map((stateFeature) => {
    const bounds = MAP_PATH.bounds(stateFeature);

    return {
      feature: stateFeature,
      name: stateFeature.properties.name,
      x0: bounds[0][0],
      y0: bounds[0][1],
      x1: bounds[1][0],
      y1: bounds[1][1]
    };
  })
  .filter((entry) => {
    return [entry.x0, entry.y0, entry.x1, entry.y1].every(Number.isFinite);
  });
const STATE_CENTERS_BY_NAME = new Map(
  STATE_RENDER_FEATURES.map((state) => [state.name, state.centroid])
);
const STATE_LABEL_FEATURES = STATE_RENDER_FEATURES.filter((state) => state.initials);
const RADIAL_LABEL_DISTANCES = [126, 164, 204, 248, 292];
const RADIAL_LABEL_ANGLES = d3.range(0, 360, 22.5)
  .map((degrees) => degrees * (Math.PI / 180));

const USAMap = ({
  locationData = [],
  loading,
  showProviderLabels = false,
  specification = DEFAULT_SPECIFICATION
}) => {
  const svgRef = useRef(null);
  const { theme } = useTheme();
  const pointColor = specification.color || DEFAULT_SPECIFICATION.color;
  const pointSize = specification.size || DEFAULT_SPECIFICATION.size;
  const pointFillOpacity = specification.fillOpacity ?? DEFAULT_SPECIFICATION.fillOpacity;
  const pointStrokeOpacity = specification.strokeOpacity ?? DEFAULT_SPECIFICATION.strokeOpacity;
  const pointStrokeWidth = specification.strokeWidth ?? DEFAULT_SPECIFICATION.strokeWidth;

  useEffect(() => {
    const svg = d3.select(svgRef.current)
      .attr('viewBox', [0, 0, MAP_WIDTH, MAP_HEIGHT])
      .attr('role', 'img')
      .attr('aria-label', 'United States billing provider claim locations');

    const sampleOtherStateCache = new Map();
    const g = svg
      .selectAll('g.usa-map-root')
      .data([null])
      .join('g')
      .attr('class', 'usa-map-root');

    g.selectAll('rect.usa-map-background')
      .data([null])
      .join('rect')
      .attr('class', 'usa-map-background')
      .attr('width', MAP_WIDTH)
      .attr('height', MAP_HEIGHT)
      .attr('fill', theme === 'dark' ? '#111827' : '#cfe5f6');

    const statesGroup = g
      .selectAll('g.state-features')
      .data([null])
      .join('g')
      .attr('class', 'state-features');

    statesGroup
      .selectAll('path')
      .data(STATE_RENDER_FEATURES, (state) => state.name)
      .join(
        (enter) => enter.append('path')
          .attr('d', (state) => state.pathD),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('fill', theme === 'dark' ? '#1f2937' : '#fbf8f3')
      .attr('stroke', theme === 'dark' ? '#4b5563' : '#d7d2cc')
      .attr('stroke-width', 0.7);

    g.selectAll('path.state-borders')
      .data([STATE_BORDERS])
      .join('path')
      .attr('class', 'state-borders')
      .attr('fill', 'none')
      .attr('stroke', theme === 'dark' ? '#374151' : '#c7c1b9')
      .attr('stroke-width', 0.8)
      .attr('stroke-linejoin', 'round');

    const stateLabelsGroup = g
      .selectAll('g.state-labels')
      .data([null])
      .join('g')
      .attr('class', 'state-labels');

    const labels = stateLabelsGroup
      .selectAll('text')
      .data(STATE_LABEL_FEATURES, (state) => state.name)
      .join(
        (enter) => enter.append('text')
          .attr('x', (state) => state.centroid[0])
          .attr('y', (state) => state.centroid[1])
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'middle')
          .attr('pointer-events', 'none')
          .text((state) => state.initials),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('fill', theme === 'dark' ? '#d1d5db' : '#7d5f5f')
      .attr('font-size', 11)
      .attr('font-weight', 700)
      .attr('letter-spacing', 0);

    const resolvedPoints = loading ? [] : locationData
      .map((point) => {
        const projectedGeo = Number.isFinite(point.lat) && Number.isFinite(point.lng)
          ? MAP_PROJECTION([point.lng, point.lat])
          : null;

        if (projectedGeo) {
          return {
            ...point,
            x: projectedGeo[0],
            y: projectedGeo[1]
          };
        }

        const stateCenter = STATE_CENTERS_BY_NAME.get(getStateName(point.state)) || null;

        if (!stateCenter || !Number.isFinite(stateCenter[0]) || !Number.isFinite(stateCenter[1])) {
          return null;
        }

        const offset = getFallbackOffset(point);

        return {
          ...point,
          x: stateCenter[0] + offset.x,
          y: stateCenter[1] + offset.y
        };
      })
      .filter(Boolean);

    const pointsGroup = g
      .selectAll('g.points-group')
      .data([null])
      .join('g')
      .attr('class', 'points-group');
    const providerLabelsGroup = g
      .selectAll('g.provider-labels-group')
      .data([null])
      .join('g')
      .attr('class', 'provider-labels-group')
      .attr('pointer-events', 'none');

    const getProviderName = (point) => {
      const providerNames = point.isCluster
        ? point.members.flatMap((member) => member.providerNames?.length ? member.providerNames : [member.name])
        : (point.providerNames?.length ? point.providerNames : [point.name]);
      const uniqueProviderNames = [...new Set(providerNames.filter(Boolean))];

      if (uniqueProviderNames.length === 0) {
        return 'Provider unavailable';
      }

      return uniqueProviderNames[0];
    };

    const getLabelText = (point) => {
      const providerName = getProviderName(point);

      if (providerName.length <= 22) {
        return providerName;
      }

      return `${providerName.slice(0, 19).trim()}...`;
    };

    const getClaimLabel = (count = 0) => {
      const claimCount = count || 0;

      return `${claimCount} ${claimCount === 1 ? 'claim' : 'claims'}`;
    };

    const getLabelStateKey = (point) => {
      const stateName = getStateName(point.state);

      return getStateInitials(stateName) || String(point.state || '').trim() || 'unknown';
    };

    const getTopBilledProviderLabelPoints = () => {
      const pointsByState = resolvedPoints.reduce((stateMap, point) => {
        const stateKey = getLabelStateKey(point);
        const statePoints = stateMap.get(stateKey) || [];

        statePoints.push(point);
        stateMap.set(stateKey, statePoints);

        return stateMap;
      }, new Map());

      return Array.from(pointsByState.values()).flatMap((statePoints) => {
        return statePoints
          .slice()
          .sort((firstPoint, secondPoint) => {
            if ((secondPoint.totalBilled || 0) !== (firstPoint.totalBilled || 0)) {
              return (secondPoint.totalBilled || 0) - (firstPoint.totalBilled || 0);
            }

            return (secondPoint.count || 0) - (firstPoint.count || 0);
          })
          .slice(0, Math.min(3, statePoints.length))
          .map((point, index) => ({
            ...point,
            labelRank: index
          }));
      });
    };

    const doRectsOverlap = (firstRect, secondRect) => {
      return firstRect.x < secondRect.x + secondRect.width
        && firstRect.x + firstRect.width > secondRect.x
        && firstRect.y < secondRect.y + secondRect.height
        && firstRect.y + firstRect.height > secondRect.y;
    };

    const getRectOverlapArea = (firstRect, secondRect) => {
      const overlapWidth = Math.max(0, Math.min(firstRect.x + firstRect.width, secondRect.x + secondRect.width) - Math.max(firstRect.x, secondRect.x));
      const overlapHeight = Math.max(0, Math.min(firstRect.y + firstRect.height, secondRect.y + secondRect.height) - Math.max(firstRect.y, secondRect.y));

      return overlapWidth * overlapHeight;
    };

    const getMapRect = (placement, transform) => ({
      x: placement.labelX + (placement.rectX / transform.k),
      y: placement.labelY + (placement.rectY / transform.k),
      width: PROVIDER_LABEL_WIDTH / transform.k,
      height: PROVIDER_LABEL_HEIGHT / transform.k
    });

    const getSampleOtherStateCacheKey = (pointStateName, sample) => {
      return `${pointStateName}|${sample[0]}|${sample[1]}`;
    };

    const isSampleInProjectedBounds = (sample, bounds) => {
      return sample[0] >= bounds.x0
        && sample[0] <= bounds.x1
        && sample[1] >= bounds.y0
        && sample[1] <= bounds.y1;
    };

    const isSampleOnOtherState = (pointStateName, sample) => {
      const cacheKey = getSampleOtherStateCacheKey(pointStateName, sample);

      if (sampleOtherStateCache.has(cacheKey)) {
        return sampleOtherStateCache.get(cacheKey);
      }

      let coordinates = null;
      let isOnOtherState = false;

      for (const entry of STATE_SPATIAL_ENTRIES) {
        if (entry.name === pointStateName || !isSampleInProjectedBounds(sample, entry)) {
          continue;
        }

        coordinates = coordinates || MAP_PROJECTION.invert(sample);

        if (!coordinates) {
          break;
        }

        if (d3.geoContains(entry.feature, coordinates)) {
          isOnOtherState = true;
          break;
        }
      }

      sampleOtherStateCache.set(cacheKey, isOnOtherState);
      return isOnOtherState;
    };

    const getOtherStateSampleCount = (point, rect) => {
      const pointStateName = getStateName(point.state);
      const samples = [
        [rect.x, rect.y],
        [rect.x + rect.width, rect.y],
        [rect.x, rect.y + rect.height],
        [rect.x + rect.width, rect.y + rect.height],
        [rect.x + rect.width / 2, rect.y + rect.height / 2]
      ];

      return samples.reduce((count, sample) => {
        return isSampleOnOtherState(pointStateName, sample) ? count + 1 : count;
      }, 0);
    };

    const getCandidateOtherStateSampleCount = (point, candidate) => {
      if (candidate.otherStateSampleCount !== undefined) {
        return candidate.otherStateSampleCount;
      }

      candidate.otherStateSampleCount = getOtherStateSampleCount(point, candidate.rect);

      return candidate.otherStateSampleCount;
    };

    const getProviderLabelCandidate = ({ point, transform, labelX, labelY, side }) => {
      if (side === 'top' || side === 'bottom') {
        const rectY = side === 'top' ? -PROVIDER_LABEL_HEIGHT : 0;

        return {
          labelX,
          labelY,
          pointLocalX: (point.x - labelX) * transform.k,
          pointLocalY: (point.y - labelY) * transform.k,
          rectX: -PROVIDER_LABEL_WIDTH / 2,
          rectY,
          lineEndX: 0,
          lineEndY: 0,
          textAnchor: 'middle',
          textX: 0,
          textY: rectY + 11
        };
      }

      const isLeft = side === 'left';

      return {
        labelX,
        labelY,
        pointLocalX: (point.x - labelX) * transform.k,
        pointLocalY: (point.y - labelY) * transform.k,
        rectX: isLeft ? -PROVIDER_LABEL_WIDTH : 0,
        rectY: -PROVIDER_LABEL_HEIGHT / 2,
        lineEndX: isLeft ? -7 : 7,
        lineEndY: 0,
        textAnchor: isLeft ? 'end' : 'start',
        textX: isLeft ? -8 : 8,
        textY: -4
      };
    };

    const getLabelSideFromAngle = (angle) => {
      const absoluteCos = Math.abs(Math.cos(angle));
      const absoluteSin = Math.abs(Math.sin(angle));

      if (absoluteCos >= absoluteSin) {
        return Math.cos(angle) < 0 ? 'left' : 'right';
      }

      return Math.sin(angle) < 0 ? 'top' : 'bottom';
    };

    const getProviderLabelCandidates = (point, transform) => {
      const rankOffset = [-38, 0, 38][point.labelRank % 3] || 0;
      const horizontalGaps = [20, 58, 96];
      const verticalGaps = [20, 54, 88];
      const sideOrder = point.x > MAP_WIDTH * 0.52
        ? ['right', 'left', 'top', 'bottom']
        : ['left', 'right', 'top', 'bottom'];
      const candidates = [];

      sideOrder.forEach((side) => {
        if (side === 'right' || side === 'left') {
          const direction = side === 'left' ? -1 : 1;

          horizontalGaps.forEach((gap) => {
            [-1, 0, 1].forEach((stackOffset) => {
              const labelX = point.x + (direction * gap) / transform.k;
              const labelY = point.y + (rankOffset + stackOffset * 18) / transform.k;

              candidates.push(getProviderLabelCandidate({
                point,
                transform,
                labelX,
                labelY,
                side
              }));
            });
          });
        }

        if (side === 'top' || side === 'bottom') {
          const direction = side === 'top' ? -1 : 1;

          verticalGaps.forEach((gap) => {
            [-1, 0, 1].forEach((stackOffset) => {
              const labelX = point.x + (stackOffset * 26) / transform.k;
              const labelY = point.y + (direction * gap + rankOffset) / transform.k;

              candidates.push(getProviderLabelCandidate({
                point,
                transform,
                labelX,
                labelY,
                side
              }));
            });
          });
        }
      });

      RADIAL_LABEL_DISTANCES.forEach((distance) => {
        RADIAL_LABEL_ANGLES.forEach((angle) => {
          const labelX = point.x + (Math.cos(angle) * distance) / transform.k;
          const labelY = point.y + (Math.sin(angle) * distance) / transform.k;

          candidates.push(getProviderLabelCandidate({
            point,
            transform,
            labelX,
            labelY,
            side: getLabelSideFromAngle(angle)
          }));
        });
      });

      return candidates;
    };

    const getProviderLabelPlacement = (point, transform, placedRects) => {
      const candidates = getProviderLabelCandidates(point, transform)
        .map((candidate) => ({
          ...candidate,
          rect: getMapRect(candidate, transform)
        }))
        .filter((candidate) => {
          return candidate.rect.x >= 4
            && candidate.rect.x + candidate.rect.width <= MAP_WIDTH - 4
            && candidate.rect.y >= 4
            && candidate.rect.y + candidate.rect.height <= MAP_HEIGHT - 4;
        });

      const nonOverlappingCandidates = candidates.filter((candidate) => {
        return !placedRects.some((placedRect) => doRectsOverlap(candidate.rect, placedRect));
      });
      const cleanCandidate = nonOverlappingCandidates.find((candidate) => {
        return getCandidateOtherStateSampleCount(point, candidate) === 0;
      });

      const fallbackCandidate = cleanCandidate
        || nonOverlappingCandidates
          .slice()
          .sort((firstCandidate, secondCandidate) => {
            return getCandidateOtherStateSampleCount(point, firstCandidate)
              - getCandidateOtherStateSampleCount(point, secondCandidate);
          })[0]
        || candidates
          .slice()
          .sort((firstCandidate, secondCandidate) => {
            const firstOverlapArea = placedRects.reduce((total, placedRect) => total + getRectOverlapArea(firstCandidate.rect, placedRect), 0);
            const secondOverlapArea = placedRects.reduce((total, placedRect) => total + getRectOverlapArea(secondCandidate.rect, placedRect), 0);

            if (firstOverlapArea !== secondOverlapArea) {
              return firstOverlapArea - secondOverlapArea;
            }

            return getCandidateOtherStateSampleCount(point, firstCandidate)
              - getCandidateOtherStateSampleCount(point, secondCandidate);
          })[0];

      if (!fallbackCandidate) {
        const clampedCandidate = getProviderLabelCandidate({
          point,
          transform,
          labelX: Math.min(Math.max(point.x + 20 / transform.k, PROVIDER_LABEL_WIDTH / transform.k), MAP_WIDTH - PROVIDER_LABEL_WIDTH / transform.k),
          labelY: Math.min(Math.max(point.y, PROVIDER_LABEL_HEIGHT / transform.k), MAP_HEIGHT - PROVIDER_LABEL_HEIGHT / transform.k),
          side: 'right'
        });

        const clampedRect = getMapRect(clampedCandidate, transform);

        placedRects.push(clampedRect);

        return {
          ...clampedCandidate,
          rect: clampedRect
        };
      }

      placedRects.push(fallbackCandidate.rect);

      return fallbackCandidate;
    };

    const renderProviderLabels = (transform = d3.zoomIdentity) => {
      sampleOtherStateCache.clear();

      const placedRects = [];
      const labelPoints = showProviderLabels
        ? getTopBilledProviderLabelPoints()
          .map((point) => ({
            ...point,
            labelPlacement: getProviderLabelPlacement(point, transform, placedRects)
          }))
        : [];

      const labelGroups = providerLabelsGroup
        .selectAll('g.provider-label')
        .data(labelPoints, (point) => point.id)
        .join(
          (enter) => {
            const labelGroup = enter.append('g')
              .attr('class', 'provider-label');

            labelGroup.append('line')
              .attr('class', 'provider-label__leader');

            labelGroup.append('rect')
              .attr('class', 'provider-label__background')
              .attr('rx', 4)
              .attr('ry', 4);

            labelGroup.append('text')
              .attr('class', 'provider-label__text')
              .append('tspan')
              .attr('class', 'provider-label__name');

            labelGroup.select('text')
              .append('tspan')
              .attr('class', 'provider-label__amount');

            return labelGroup;
          },
          (update) => update,
          (exit) => exit.remove()
        );

      labelGroups.each(function(point) {
        const labelGroup = d3.select(this);
        const placement = point.labelPlacement;

        labelGroup.attr('transform', `translate(${placement.labelX}, ${placement.labelY}) scale(${1 / transform.k})`);

        labelGroup.select('line')
          .attr('x1', placement.pointLocalX)
          .attr('y1', placement.pointLocalY)
          .attr('x2', placement.lineEndX)
          .attr('y2', placement.lineEndY)
          .attr('stroke', theme === 'dark' ? '#9ca3af' : '#658f89')
          .attr('stroke-width', 1)
          .attr('stroke-linecap', 'round');

        labelGroup.select('rect')
          .attr('x', placement.rectX)
          .attr('y', placement.rectY)
          .attr('width', PROVIDER_LABEL_WIDTH)
          .attr('height', PROVIDER_LABEL_HEIGHT)
          .attr('fill', theme === 'dark' ? 'rgba(17, 24, 39, 0.86)' : 'rgba(255, 255, 255, 0.86)')
          .attr('stroke', theme === 'dark' ? '#4b5563' : '#c8d8d5')
          .attr('stroke-width', 1);

        labelGroup.select('text')
          .attr('x', placement.textX)
          .attr('y', placement.textY)
          .attr('text-anchor', placement.textAnchor)
          .attr('fill', theme === 'dark' ? '#f9fafb' : '#28413e')
          .attr('font-size', 9.5)
          .attr('font-weight', 700)
          .attr('letter-spacing', 0);

        labelGroup.select('.provider-label__name')
          .attr('x', placement.textX)
          .attr('dy', 0)
          .text(getLabelText(point));

        labelGroup.select('.provider-label__amount')
          .attr('x', placement.textX)
          .attr('dy', 12)
          .attr('fill', theme === 'dark' ? '#cbd5e1' : '#5d7470')
          .attr('font-size', 8.5)
          .attr('font-weight', 600)
          .text(`${formatCodeAmount(point.totalBilled || 0)} | ${getClaimLabel(point.count)}`);
      });
    };

    const zoomToPoint = (event, point) => {
      event.stopPropagation();

      const currentTransform = d3.zoomTransform(svg.node());
      const nextScale = Math.min(8, Math.max(currentTransform.k + 1, currentTransform.k * 1.7));
      const nextTransform = d3.zoomIdentity
        .translate(MAP_WIDTH / 2 - point.x * nextScale, MAP_HEIGHT / 2 - point.y * nextScale)
        .scale(nextScale);

      svg.transition()
        .duration(500)
        .ease(d3.easeCubicOut)
        .call(zoom.transform, nextTransform);
    };

    let zoomFrame = null;
    let pendingZoomTransform = null;
    let labelPlacementTimer = null;

    const renderPoints = (transform = d3.zoomIdentity, shouldRenderLabels = true) => {
      const clusters = createPointClusters(resolvedPoints, transform.k);

      pointsGroup
        .selectAll('circle')
        .data(clusters, (point) => point.id)
        .join(
          (enter) => {
            const circle = enter.append('circle')
              .attr('class', 'claim-location-dot')
              .attr('fill', pointColor)
              .attr('stroke', theme === 'dark' ? '#b7f3e6' : '#2d8277')
              .attr('stroke-opacity', pointStrokeOpacity)
              .style('cursor', 'pointer')
              .on('click', zoomToPoint);

            return circle;
          },
          (update) => update,
          (exit) => exit.remove()
        )
        .attr('aria-label', (point) => getProviderName(point))
        .attr('cx', (point) => point.x)
        .attr('cy', (point) => point.y)
        .attr('r', (point) => {
          const radius = point.isCluster
            ? getClusterRadius(point, pointSize)
            : getPointRadius(point, pointSize);

          return radius / transform.k;
        })
        .attr('fill-opacity', (point) => point.isCluster ? 0.72 : pointFillOpacity)
        .attr('stroke-width', (point) => {
          return (point.isCluster ? pointStrokeWidth + 0.6 : pointStrokeWidth) / transform.k;
        })
        .each(function() {
          d3.select(this).select('title').remove();
        });

      if (shouldRenderLabels) {
        renderProviderLabels(transform);
      }
    };

    const renderZoomFrame = (transform) => {
      g.attr('transform', transform);
      labels.attr('display', transform.k <= 1.08 ? null : 'none');
      labels.attr('font-size', 11 / transform.k);
      renderPoints(transform, false);
    };

    const scheduleProviderLabelRender = (transform) => {
      if (!showProviderLabels) {
        providerLabelsGroup.selectAll('g.provider-label').remove();
        return;
      }

      providerLabelsGroup.attr('display', 'none');

      if (labelPlacementTimer) {
        window.clearTimeout(labelPlacementTimer);
      }

      labelPlacementTimer = window.setTimeout(() => {
        renderProviderLabels(transform);
        providerLabelsGroup.attr('display', null);
        labelPlacementTimer = null;
      }, 120);
    };

    const zoom = d3.zoom()
      .scaleExtent([1, 8])
      .on('zoom', (event) => {
        pendingZoomTransform = event.transform;
        scheduleProviderLabelRender(pendingZoomTransform);

        if (zoomFrame) {
          return;
        }

        zoomFrame = window.requestAnimationFrame(() => {
          renderZoomFrame(pendingZoomTransform);
          zoomFrame = null;
        });
      });

    svg.call(zoom);
    const initialTransform = d3.zoomTransform(svg.node());
    renderZoomFrame(initialTransform);
    scheduleProviderLabelRender(initialTransform);

    svg.on('dblclick', () => {
      svg.transition()
        .duration(500)
        .ease(d3.easeCubicOut)
        .call(zoom.transform, d3.zoomIdentity);
    });

    return () => {
      if (zoomFrame) {
        window.cancelAnimationFrame(zoomFrame);
      }
      if (labelPlacementTimer) {
        window.clearTimeout(labelPlacementTimer);
      }
      svg.on('.zoom', null);
      svg.on('dblclick', null);
    };
  }, [
    theme,
    locationData,
    loading,
    showProviderLabels,
    pointColor,
    pointFillOpacity,
    pointSize,
    pointStrokeOpacity,
    pointStrokeWidth
  ]);

  return (
    <Box
      sx={{
        width: '100%',
        height: 'clamp(420px, 68vh, 760px)',
        borderRadius: 1,
        overflow: 'hidden',
        background: theme === 'dark' ? '#111827' : '#cfe5f6',
        border: '1px solid var(--border-color)',
        position: 'relative',
        '& svg': {
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: 'grab',
          '&:active': {
            cursor: 'grabbing'
          }
        }
      }}
    >
      <svg ref={svgRef} />
    </Box>
  );
};

export default USAMap;
